'use client';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { faqPageSchema, jsonLdScript } from '@/lib/schema-org';
import { track } from '@/lib/analytics/ym';
import { SITE } from '@/lib/site-config';
import { FadeUp } from '@/components/motion';

type QA = {
  id: string;
  q: string;
  a: React.ReactNode;
};

// Plain-text answers for FAQPage JSON-LD (no markup).
// Источник: copy/FAQ.md → Schema.org JSON-LD draft.
const FAQ_SCHEMA_ITEMS: { question: string; answer: string }[] = [
  {
    question: 'Сколько стоит проект и как формируется цена?',
    answer:
      'Стоимость рассчитывается от объёма задач: автоматизировать локальный процесс под ключ всегда выгоднее, чем перестраивать всё направление целиком. Ориентировочные границы: «Разведка» — от 50 тыс. руб. (1–2 недели), «Пилот» — от 150 тыс. руб. (1–2 месяца), «Трансформация» — от 400 тыс. руб. (3–6 месяцев). Точные расчёты и 3 варианта сотрудничества (Базовый, Рекомендуемый и Премиум) готовим индивидуально — в коммерческом предложении после знакомства и бесплатного экспресс-аудита. Мини-аудит — за 2–3 дня, без обязательств.',
  },
  {
    question: 'С чего начать, если ещё не понимаю, что автоматизировать?',
    answer:
      'Это абсолютно нормальная точка старта. Специально для таких ситуаций у нас разработан пакет «Разведка» (1–2 недели). Мы изучим ваши текущие процессы и соберём карту решений: где ИИ действительно принесёт окупаемость, а где достаточно простого скрипта или регламента. На выходе — документ с конкретными рекомендациями, оценкой потенциального эффекта и приоритетами. Стоимость пакета — от 50 000 руб. Начните с бесплатной 30-минутной встречи в формате интервью — никакой подготовки не требуется.',
  },
  {
    question: 'Работаете с Bitrix24, 1С, amoCRM?',
    answer:
      `Да, это стандартный технологический стек для большинства наших клиентов. Мы создаём AI-надстройки над текущими системами без смены платформы — карточки, телефония, документооборот остаются на месте, ИИ встраивается поверх. Кейс (май 2026): производитель промышленного оборудования, 130 менеджеров, RAG-ассистент в Bitrix24 на 11 000 файлах (PDF, DOCX, PPTX) и 60 видео ~120 ГБ. amoCRM — через стандартные API, технические нюансы и объём работ обсуждаем на вводном звонке, чтобы предложить наиболее надёжное и стабильное решение.`,
  },
  {
    question: 'Какие гарантии результата?',
    answer:
      'Мы гарантируем надёжность не обещаниями, а понятными условиями совместной работы. Шаг 1: бесплатный экспресс-аудит за 2–3 дня — документ с описанием ситуации («что мы увидели»), предложениями и ссылкой на похожий кейс, без оплаты и обязательств. Шаг 2: безопасная оплата — 50% предоплата, остаток после сдачи этапа и подписания закрывающих документов; на проектах от 200 тыс. руб. — промежуточные шаги. Шаг 3: жёсткие границы в договоре — точный объём фиксируется юридически, любые новые требования оформляются допсоглашением.',
  },
  {
    question: 'Можно ли посмотреть код и получить исходники?',
    answer:
      'Да. Вопрос владения кодом напрямую влияет на безопасность и независимость вашего бизнеса, поэтому мы фиксируем передачу всех материалов в договоре. По завершении каждого этапа вы получаете полный исходный код и необходимую техническую документацию. Продукт разворачивается непосредственно в вашем контуре и переходит в вашу полную собственность — никаких «чёрных ящиков» и скрытых подписок. Формат передачи исходников и архитектуру решения детально обсуждаем на вводном звонке.',
  },
  {
    question: 'Как защищены данные и где живут серверы (152-ФЗ)?',
    answer:
      `Для промышленных предприятий и АПК информационная безопасность — это первый и самый важный фильтр. Все решения проектируются строго с учётом требований 152-ФЗ «О персональных данных». Системы разворачиваются либо на серверах заказчика (On-Premise), либо в защищённом российском облачном контуре (VPS в отечественных ЦОД, Docker, изолированная среда). Кейс с производителем оборудования (130 менеджеров): RAG-ассистент и аналитика звонков — на серверах в РФ, без западных облаков. Кейс для молочной фермы (КРС): CV-стек (EfficientNet-B1, ByteTrack, YOLOv11, RTMPose) на edge-компьютере NVIDIA Jetson Orin NX на объекте — видеопоток обрабатывается локально, интернет не нужен.`,
  },
  {
    question: 'Сколько времени до первых результатов?',
    answer:
      'Сроки зависят от масштаба проекта. «Разведка» — аналитический документ с приоритетами и оценкой эффекта за 1–2 недели. «Пилот» — автоматизация одного процесса под ключ в проде за 1–2 месяца. «Трансформация» — комплексная модернизация направления за 3–6 месяцев. Первые результаты взаимодействия вы увидите уже через 2–3 дня после знакомства: бесплатный письменный экспресс-аудит. Кейс производителя станков (RAG на 11 000 файлах) — в проде с мая 2026. Кейс молочной фермы — 4 этапа по 1–3 месяца каждый.',
  },
  {
    question: 'Работаете с физлицами и стартапами без выручки?',
    answer:
      'Нет. Мы работаем только с юридическими лицами и ИП, минимальная стоимость проекта — 100 000 рублей. Наш бутиковый формат предполагает личное участие основателя в проектировании и сдаче каждого решения (founder-led delivery). При бюджете ниже 100 тыс. рублей такая модель становится экономически нецелесообразной. Если ваш бюджет пока меньше — воспользуйтесь нашими бесплатными материалами: чек-лист готовности к AI-автоматизации доступен в Hero-секции.',
  },
];

const items: QA[] = [
  {
    id: 'price',
    q: 'Сколько стоит проект и как формируется цена?',
    a: (
      <div>
        <p>
          Мы понимаем, что бюджет должен соотноситься с результатами и
          окупаемостью (ROI) проекта. Стоимость в {SITE.brand} рассчитывается
          от объёма задач: автоматизировать локальный процесс под ключ всегда
          выгоднее, чем перестраивать всё направление целиком.
        </p>
        <p className="mt-3 font-semibold">Ориентировочные границы для наших пакетов:</p>
        <ul className="mt-2 space-y-1 list-disc list-inside">
          <li><span className="font-semibold">«Разведка»</span> — от 50 тыс. руб. (1–2 недели)</li>
          <li><span className="font-semibold">«Пилот»</span> — от 150 тыс. руб. (1–2 месяца)</li>
          <li><span className="font-semibold">«Трансформация»</span> — от 400 тыс. руб. (3–6 месяцев)</li>
        </ul>
        <p className="mt-3">
          Точные расчёты и 3 варианта сотрудничества (Базовый, Рекомендуемый и
          Премиум) мы готовим индивидуально — в коммерческом предложении после
          знакомства и бесплатного экспресс-аудита.
        </p>
        <p className="mt-3">
          Оставьте заявку на мини-аудит. Мы проведём его за 2–3 дня, это вас
          ни к чему не обяжет.
        </p>
      </div>
    ),
  },
  {
    id: 'start',
    q: 'С чего начать, если ещё не понимаю, что автоматизировать?',
    a: (
      <div>
        <p>
          Это абсолютно нормальная точка старта. Специально для таких ситуаций
          у нас разработан пакет{' '}
          <span className="font-semibold">«Разведка»</span> (1–2 недели).
        </p>
        <p className="mt-3">
          Мы изучим ваши текущие процессы и соберём карту решений. Вы увидите,
          где внедрение ИИ действительно принесёт окупаемость, а где будет
          достаточно простого скрипта или изменения регламента. На выходе вы
          получите документ с конкретными рекомендациями, оценкой потенциального
          эффекта и приоритетами.
        </p>
        <p className="mt-3">Стоимость пакета — от 50 000 руб.</p>
        <p className="mt-3">
          Начните с короткой бесплатной встречи на 30 минут. Никакой подготовки
          с вашей стороны не требуется — мы сами проведём созвон в формате
          интервью и зададим наводящие вопросы.
        </p>
      </div>
    ),
  },
  {
    id: 'crm',
    q: 'Работаете с Bitrix24 / 1С / amoCRM?',
    a: (
      <div>
        <p>
          Да, это стандартный технологический стек для большинства наших
          клиентов. Мы создаём AI-надстройки над вашими текущими системами без
          необходимости менять привычную платформу. Карточки клиентов,
          телефония, документооборот — всё остаётся на месте, а искусственный
          интеллект бесшовно встраивается поверх них.
        </p>
        <p className="mt-3 font-semibold">
          Пример из практики (внедрён в мае 2026 года):
        </p>
        <p className="mt-1">
          Для крупного производителя промышленного оборудования со штатом в 130
          менеджеров мы разработали умного ассистента прямо внутри Bitrix24.
          Система работает по технологии RAG и мгновенно находит ответы в базе
          знаний компании, состоящей из 11 000 файлов (PDF, DOCX, PPTX) и 60
          видеоматериалов общим объёмом около 120 ГБ. Подробнее об этом проекте
          можно прочитать в разделе «Кейсы».
        </p>
        <p className="mt-3 font-semibold">Что касается amoCRM:</p>
        <p className="mt-1">
          Интеграцию с этой системой мы реализуем через стандартные API.
          Поскольку каждый бизнес-процесс уникален, технические нюансы и объём
          работ мы детально обсуждаем на вводном звонке, чтобы предложить
          наиболее надёжное и стабильное решение.
        </p>
      </div>
    ),
  },
  {
    id: 'guarantee',
    q: 'Какие гарантии результата?',
    a: (
      <div>
        <p>
          Мы гарантируем надёжность не обещаниями, а понятными условиями
          совместной работы:
        </p>
        <ul className="mt-3 space-y-3 list-disc list-inside">
          <li>
            <span className="font-semibold">Шаг 1. Бесплатный тест-драйв.</span>{' '}
            Мы проводим экспресс-аудит за 2–3 дня. Вы получаете документ с
            описанием текущей ситуации («что мы увидели»), нашими предложениями
            и ссылкой на похожий кейс из практики. Без оплаты и обязательств.
          </li>
          <li>
            <span className="font-semibold">Шаг 2. Безопасная оплата.</span>{' '}
            Стандартная схема — 50% предоплата, остаток — после сдачи этапа и
            подписания закрывающих документов. На проектах с бюджетом от 200
            тыс. рублей мы делим оплату на промежуточные шаги.
          </li>
          <li>
            <span className="font-semibold">
              Шаг 3. Жёсткие границы в договоре.
            </span>{' '}
            Точный объём задач фиксируется юридически. Любые новые требования
            оформляются как дополнительное соглашение, что защищает вас от
            непредвиденного роста сметы.
          </li>
        </ul>
        <p className="mt-3">
          Оставьте заявку на бесплатный аудит, чтобы лично оценить наш формат
          работы.
        </p>
      </div>
    ),
  },
  {
    id: 'code',
    q: 'Можно ли посмотреть код / получить исходники?',
    a: (
      <div>
        <p>
          Да. Вопрос владения кодом напрямую влияет на безопасность и
          независимость вашего бизнеса, поэтому мы фиксируем передачу всех
          материалов в договоре.
        </p>
        <p className="mt-3">
          По завершении каждого этапа проекта вы получаете:
        </p>
        <ul className="mt-2 space-y-1 list-disc list-inside">
          <li>Полный исходный код решения.</li>
          <li>Необходимую техническую документацию для поддержки.</li>
        </ul>
        <p className="mt-3">
          Продукт разворачивается непосредственно в вашем контуре и переходит в
          вашу полную собственность — никаких «чёрных ящиков» и скрытых
          подписок. Вы сохраняете независимость от нас как от интегратора.
        </p>
        <p className="mt-3">
          Запишитесь на вводный 30-минутный созвон, чтобы детально обсудить
          формат передачи исходников и архитектуру решения.
        </p>
      </div>
    ),
  },
  {
    id: 'data',
    q: 'Как защищены данные? Где живут серверы (152-ФЗ)?',
    a: (
      <div>
        <p>
          Мы понимаем, что для промышленных предприятий и АПК информационная
          безопасность — это первый и самый важный фильтр.
        </p>
        <p className="mt-3">
          Все наши решения проектируются строго с учётом требований{' '}
          <span className="font-semibold">152-ФЗ «О персональных данных»</span>.
          Мы разворачиваем системы либо на серверах заказчика (On-Premise), либо
          в защищённом российском облачном контуре (VPS в отечественных ЦОД,
          Docker, полностью изолированная среда).
        </p>
        <p className="mt-3 font-semibold">Примеры архитектуры из нашей практики:</p>
        <ul className="mt-2 space-y-2 list-disc list-inside">
          <li>
            <span className="font-semibold">Кейс с производителем оборудования (130 менеджеров):</span>{' '}
            RAG-ассистент и аналитика звонков развёрнуты на серверах внутри РФ
            в изолированном Docker-контуре, без обращения к западным облачным
            сервисам.
          </li>
          <li>
            <span className="font-semibold">Кейс для молочной фермы (КРС):</span>{' '}
            CV-стек (EfficientNet-B1, ByteTrack, YOLOv11, RTMPose) установлен
            на edge-компьютере NVIDIA Jetson Orin NX на объекте. Видеопоток
            обрабатывается локально — стабильный интернет для работы ИИ не
            требуется.
          </li>
        </ul>
        {/* PROOF:NEED: текст /privacy 152-ФЗ — оператор = ИП */}
        <p className="mt-3">
          Мы готовы подробно обсудить архитектуру безопасности и требования к
          защите данных на первом звонке.
        </p>
      </div>
    ),
  },
  {
    id: 'time',
    q: 'Сколько времени до первых результатов?',
    a: (
      <div>
        <p>Сроки зависят от масштаба проекта и выбранного пакета услуг:</p>
        <ul className="mt-2 space-y-1 list-disc list-inside">
          <li>
            <span className="font-semibold">«Разведка»</span> — готовый
            аналитический документ с приоритетами и оценкой эффекта за 1–2
            недели.
          </li>
          <li>
            <span className="font-semibold">«Пилот»</span> — автоматизация одного
            процесса под ключ с запуском в промышленную эксплуатацию за 1–2
            месяца.
          </li>
          <li>
            <span className="font-semibold">«Трансформация»</span> — комплексная
            модернизация выбранного направления за 3–6 месяцев.
          </li>
        </ul>
        <p className="mt-3">
          При этом первые результаты нашего взаимодействия вы увидите уже через
          2–3 дня после знакомства — мы подготовим для вас бесплатный
          письменный экспресс-аудит.
        </p>
        <p className="mt-3">
          Внедрение всегда происходит поэтапно. В кейсе производителя станков
          первая фаза (RAG-ассистент на базе 11 000 документов) была полностью
          выведена в прод в мае 2026 года. В проекте для молочной фермы мы
          разделили автоматизацию на 4 независимых этапа по 1–3 месяца каждый.
        </p>
        <p className="mt-3">
          Оставьте заявку на бесплатный мини-аудит, чтобы получить точную
          оценку сроков под ваш бизнес-процесс.
        </p>
      </div>
    ),
  },
  {
    id: 'individuals',
    q: 'Работаете с физлицами и стартапами без выручки?',
    a: (
      <div>
        <p>
          Нет. Мы работаем только с юридическими лицами и ИП, а минимальная
          стоимость проекта составляет 100 000 рублей.
        </p>
        <p className="mt-3">
          Дело не в снобизме: наш бутиковый формат работы предполагает личное
          участие основателя в проектировании и сдаче каждого решения (
          <em>founder-led delivery</em>). При бюджете ниже 100 тыс. рублей такая
          модель становится экономически нецелесообразной, учитывая время на
          вводные созвоны, проектирование и качественный бесплатный аудит.
        </p>
        <p className="mt-3">
          Если ваш бюджет пока меньше этой суммы, рекомендуем воспользоваться
          нашими бесплатными материалами. В чек-листе готовности к
          AI-автоматизации мы собрали практические советы, которые помогут
          разобраться самостоятельно. Скачать его можно в Hero-секции.
        </p>
      </div>
    ),
  },
];

export function FAQ() {
  return (
    <section id="faq" className="bg-muted/40">
      <div className="container mx-auto max-w-4xl px-4 md:px-6 lg:px-8 py-24 md:py-32">
        <FadeUp>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Частые вопросы
          </p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
            8 вопросов, которые задают чаще всего
          </h2>
        </FadeUp>
        <FadeUp delay={0.2}>
          <Accordion className="mt-8">
            {items.map((qa) => (
              <AccordionItem key={qa.id} value={qa.id}>
                <AccordionTrigger
                  className="py-4 text-base md:text-lg"
                  onClick={() => track.faqOpen(qa.id)}
                >
                  {qa.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm md:text-base text-muted-foreground">
                  {qa.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeUp>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: jsonLdScript(faqPageSchema(FAQ_SCHEMA_ITEMS)),
          }}
        />
      </div>
    </section>
  );
}
